import { useCallback, useEffect, useRef, useState } from "react";
import { m } from "framer-motion";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Clock, Mail, Phone } from "lucide-react";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { PricingEnquiryForm } from "@/components/sections/PricingEnquiryForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cardReveal } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { CONTACT } from "@/data/contact";
import {
  COMPARISON,
  DEFAULT_PLAN,
  GENERAL_TERMS,
  PLANS,
  PLAN_BY_ID,
  PRICING_FAQS,
  type PlanId,
} from "@/data/pricing";

const SITE_URL = "https://www.hbtintegrisys.com";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": `${SITE_URL}/pricing#service`,
      name: "Website Development",
      serviceType: "Website development",
      url: `${SITE_URL}/pricing`,
      // The business itself is defined once in index.html's JSON-LD.
      provider: { "@id": `${SITE_URL}/#business` },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Website development plans",
        itemListElement: PLANS.map((p) => ({
          "@type": "Offer",
          name: p.fullName,
          description: p.tagline,
          itemOffered: { "@type": "Service", name: p.fullName, description: p.intro },
          // Prices come from the plan data, so the schema can't drift from the page.
          ...(p.priceRange &&
            (p.priceRange.min === p.priceRange.max
              ? { price: String(p.priceRange.min), priceCurrency: "INR" }
              : {
                  priceCurrency: "INR",
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    minPrice: p.priceRange.min,
                    maxPrice: p.priceRange.max,
                    priceCurrency: "INR",
                  },
                })),
        })),
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: PRICING_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function Pricing() {
  const [plan, setPlan] = useState<PlanId>(DEFAULT_PLAN);
  const [message, setMessage] = useState(PLAN_BY_ID[DEFAULT_PLAN].draft);
  // Once the visitor edits the drafted message, switching plans no longer
  // overwrites it; the enquiry button always re-drafts.
  const [messageEdited, setMessageEdited] = useState(false);
  const formSectionRef = useRef<HTMLElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const scrollTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const planIndex = PLANS.findIndex((p) => p.id === plan);

  const selectPlan = (id: PlanId) => {
    setPlan(id);
    if (!messageEdited) setMessage(PLAN_BY_ID[id].draft);
  };

  // The debounced swipe handler below runs after later renders, so it reads
  // the latest plan and selectPlan through this ref.
  const latest = useRef({ plan, selectPlan });
  latest.current = { plan, selectPlan };

  const stepPlan = (dir: -1 | 1) => {
    const next = PLANS[planIndex + dir];
    if (next) selectPlan(next.id);
  };

  // Centre the selected plan's details card. Scrolls only the carousel, never
  // the page, so choosing a plan at the top doesn't jump the visitor down.
  const centreDetails = useCallback((id: PlanId, behavior: ScrollBehavior) => {
    const scroller = detailsRef.current;
    const card = scroller?.querySelector<HTMLElement>(`[data-plan="${id}"]`);
    if (!scroller || !card) return;
    scroller.scrollTo({ left: card.offsetLeft - (scroller.clientWidth - card.clientWidth) / 2, behavior });
  }, []);

  const firstCentre = useRef(true);
  useEffect(() => {
    centreDetails(plan, firstCentre.current ? "instant" : "smooth");
    firstCentre.current = false;
  }, [plan, centreDetails]);

  useEffect(() => {
    const onResize = () => centreDetails(plan, "instant");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [plan, centreDetails]);

  // When the visitor swipes the carousel, select the plan that settles in
  // the centre (debounced, so passing over a card mid-swipe doesn't count).
  const onDetailsScroll = () => {
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      const scroller = detailsRef.current;
      if (!scroller) return;
      const centre = scroller.scrollLeft + scroller.clientWidth / 2;
      const { plan: current, selectPlan: select } = latest.current;
      let closest: PlanId = current;
      let best = Infinity;
      scroller.querySelectorAll<HTMLElement>("[data-plan]").forEach((card) => {
        const d = Math.abs(card.offsetLeft + card.clientWidth / 2 - centre);
        if (d < best) {
          best = d;
          closest = card.dataset.plan as PlanId;
        }
      });
      if (closest !== current) select(closest);
    }, 150);
  };

  const editMessage = (text: string) => {
    setMessage(text);
    setMessageEdited(text !== PLAN_BY_ID[plan].draft);
  };

  const startEnquiry = () => {
    setMessage(PLAN_BY_ID[plan].draft);
    setMessageEdited(false);
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    nameRef.current?.focus({ preventScroll: true });
  };

  const selected = PLAN_BY_ID[plan];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Seo
        title="Website Development Pricing & Plans"
        description={`Website pricing from HBT Integrisys: a free business website, Standard websites from ${PLAN_BY_ID.standard.price}, and custom web applications quoted by scope for your business.`}
        keywords={[
          "website development packages and prices",
          "free business website for small businesses",
          "affordable website design cost for small business",
          "business website package with hosting and SEO",
          "custom web application development quote",
          "Google Business Profile setup for small businesses",
        ]}
        path="/pricing"
      />
      <script
        type="application/ld+json"
        // Escape "<" so page text can never close the script tag.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }}
      />
      <Navbar />

      <main className="flex-1">
        <PageHero
          eyebrow="Pricing"
          title="Website Development Pricing"
          description="Website plans for every stage of your business. Start with a free business website, grow into a professional Standard website, or get a custom solution built around how your business works."
        />

        {/* Plans */}
        <section className="py-20 md:py-30 bg-muted">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <p className="font-mono text-[13px] font-normal tracking-[.14em] uppercase text-accent">Choose a plan</p>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-[-0.03em] text-foreground">
                Simple, transparent website pricing
              </h2>
              <p className="text-muted-foreground text-lg">
                Clear prices for small and growing businesses. Select
                the plan that fits your business, then send us an enquiry — we'll draft the message
                for you.
              </p>
            </div>

            <fieldset className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <legend className="sr-only">Select a plan</legend>
              {PLANS.map((p, idx) => {
                const isSelected = plan === p.id;
                return (
                  <m.label
                    key={p.id}
                    {...cardReveal(idx, 3)}
                    className={cn(
                      "card-fx relative flex flex-col cursor-pointer bg-card rounded-[20px] border-2 p-7 shadow-sm transition-colors focus-within:ring-2 focus-within:ring-accent focus-within:ring-offset-2",
                      isSelected ? "border-accent shadow-lg shadow-accent/10" : "border-border/60 hover:border-accent/40"
                    )}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={p.id}
                      checked={isSelected}
                      onChange={() => selectPlan(p.id)}
                      className="sr-only"
                    />
                    {p.id === "standard" && (
                      <span className="absolute -top-3 left-7 rounded-full bg-accent px-3 py-1 text-xs font-bold text-white">
                        Recommended
                      </span>
                    )}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h3 className="text-xl font-display font-semibold text-foreground">{p.name}</h3>
                      <span
                        aria-hidden="true"
                        className={cn(
                          "w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                          isSelected ? "border-accent bg-accent text-white" : "border-border"
                        )}
                      >
                        {isSelected && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                      </span>
                    </div>
                    <p className="text-3xl font-display font-semibold tracking-[-0.03em] text-foreground">{p.price}</p>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">{p.priceNote}</p>
                    <p className="text-foreground/80 mb-6">{p.tagline}</p>
                    <ul className="space-y-3 text-sm text-foreground/80">
                      {p.highlights.map((h) => (
                        <li key={h} className="flex gap-2.5">
                          <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </m.label>
                );
              })}
            </fieldset>
          </div>
        </section>

        {/* Plan details */}
        <section className="py-20 md:py-30 bg-background">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <p className="font-mono text-[13px] font-normal tracking-[.14em] uppercase text-accent">Plan details</p>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-[-0.03em] text-foreground">
                What each plan includes
              </h2>
            </div>
          </div>

          {/* Full-width scroller so the cards either side of the selected one
              peek in. The inline padding lets the first and last cards centre.
              The arrows sit over the left and right edges, vertically centred. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => stepPlan(-1)}
              disabled={planIndex === 0}
              aria-label="Previous plan"
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border border-border bg-card/95 backdrop-blur shadow-lg flex items-center justify-center text-foreground hover:text-accent hover:border-accent transition-colors disabled:opacity-0 disabled:pointer-events-none"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => stepPlan(1)}
              disabled={planIndex === PLANS.length - 1}
              aria-label="Next plan"
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full border border-border bg-card/95 backdrop-blur shadow-lg flex items-center justify-center text-foreground hover:text-accent hover:border-accent transition-colors disabled:opacity-0 disabled:pointer-events-none"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div
              ref={detailsRef}
              onScroll={onDetailsScroll}
              // Focusable so keyboard users can scroll it with the arrow keys
              // (axe: scrollable-region-focusable).
              tabIndex={0}
              role="region"
              aria-label="Plan details"
              className="relative flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset gap-6 overflow-x-auto snap-x snap-mandatory pb-4 px-[calc(50%-42vw)] md:px-[calc(50%-21rem)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {PLANS.map((p) => (
                <article
                  key={p.id}
                  id={`plan-${p.id}`}
                  data-plan={p.id}
                  onClick={() => selectPlan(p.id)}
                  className={cn(
                    "snap-center shrink-0 w-[84vw] md:w-[42rem] bg-card rounded-[20px] border-2 p-6 md:p-9 shadow-sm transition-[opacity,border-color] duration-300",
                    plan === p.id ? "border-accent" : "border-border/60 opacity-60 cursor-pointer hover:opacity-80"
                  )}
                >
                  <h3 className="text-2xl font-display font-semibold tracking-[-0.03em] text-foreground mb-3">{p.heading}</h3>
                  <p className="text-foreground/80 leading-relaxed mb-8">{p.intro}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-display font-semibold text-foreground mb-4">What's included</h4>
                      <ul className="space-y-3 text-sm text-foreground/80">
                        {p.included.map((item) => (
                          <li key={item} className="flex gap-2.5">
                            <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <dl className="space-y-4 text-sm">
                      {p.terms.map((t) => (
                        <div key={t.label}>
                          <dt className="font-display font-semibold text-foreground mb-1">{t.label}</dt>
                          <dd className="text-foreground/80 leading-relaxed">{t.text}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <p className="text-sm italic text-muted-foreground mt-8 pt-6 border-t border-border/60">
                    {p.footnote}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="text-center mt-10">
              <Button
                variant="ghost"
                type="button"
                onClick={startEnquiry}
                className="btn-fx h-14 px-8 bg-brand hover:bg-brand text-white rounded-full font-semibold hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_rgba(91,124,250,.8)] transition-all text-base shadow-lg shadow-accent/20"
              >
                Enquire about the {selected.name} plan <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                We'll prefill your enquiry — just add your name and contact number.
              </p>
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section className="py-20 md:py-30 bg-muted">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
              <p className="font-mono text-[13px] font-normal tracking-[.14em] uppercase text-accent">Compare plans</p>
              <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-[-0.03em] text-foreground">
                Free vs Standard vs Custom
              </h2>
            </div>

            <div className="max-w-6xl mx-auto overflow-x-auto rounded-[20px] border border-border/60 bg-card shadow-sm">
              <table className="w-full min-w-[720px] text-left text-sm">
                <caption className="sr-only">Comparison of the Free, Standard and Custom website plans</caption>
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className="p-4 md:p-5 font-display font-semibold text-foreground w-[19%]">
                      Feature
                    </th>
                    {PLANS.map((p) => (
                      <th
                        key={p.id}
                        scope="col"
                        className={cn(
                          "p-4 md:p-5 font-display font-semibold text-base transition-colors",
                          plan === p.id ? "bg-accent/10 text-accent" : "text-foreground"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => selectPlan(p.id)}
                          aria-pressed={plan === p.id}
                          className="hover:text-accent transition-colors"
                        >
                          {p.name}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row) => (
                    <tr key={row.feature} className="border-b border-border/60 last:border-0">
                      <th scope="row" className="p-4 md:p-5 font-semibold text-foreground align-top">
                        {row.feature}
                      </th>
                      {PLANS.map((p) => (
                        <td
                          key={p.id}
                          className={cn(
                            "p-4 md:p-5 text-foreground/80 align-top transition-colors",
                            plan === p.id && "bg-accent/5"
                          )}
                        >
                          {row.values[p.id]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="max-w-3xl mx-auto text-center text-muted-foreground mt-8 leading-relaxed">
              <strong className="text-foreground">Not sure Standard is enough?</strong> Custom isn't
              simply a bigger website — it's for requirements that can't reasonably fit the
              Standard package, like bookings, customer logins, payments, dashboards or integrations
              with your existing systems. We scope it with you and quote fairly for exactly what you
              need. See how we approach{" "}
              <Link
                href="/services/web-development"
                className="font-semibold text-accent hover:underline underline-offset-4"
              >
                custom web application development
              </Link>
              .
            </p>
          </div>
        </section>

        {/* Terms */}
        <section id="terms" className="py-20 md:py-30 bg-background">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="max-w-5xl mx-auto">
              <article className="bg-card rounded-[20px] border border-border/60 p-7 md:p-10 shadow-sm">
                <h2 className="text-2xl md:text-3xl font-display font-semibold tracking-[-0.03em] text-foreground mb-6">
                  Terms and conditions
                </h2>
                <ol className="space-y-4 text-foreground/80 leading-relaxed list-decimal pl-5 marker:text-accent marker:font-bold">
                  {GENERAL_TERMS.map((term) => (
                    <li key={term} className="pl-1">{term}</li>
                  ))}
                </ol>
              </article>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 md:py-30 bg-muted">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12 space-y-3">
                <p className="font-mono text-[13px] font-normal tracking-[.14em] uppercase text-accent">Got Questions?</p>
                <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-[-0.03em] text-foreground">
                  Pricing FAQ
                </h2>
              </div>

              <Accordion
                type="single"
                collapsible
                defaultValue="item-0"
                className="w-full bg-card rounded-[20px] border border-border/50 shadow-sm p-2 md:p-4"
              >
                {PRICING_FAQS.map((faq, idx) => (
                  <AccordionItem key={faq.q} value={`item-${idx}`} className="border-b last:border-0">
                    <AccordionTrigger className="text-left font-display font-semibold text-lg hover:text-accent px-4 py-6">
                      {faq.q}
                    </AccordionTrigger>
                    {/* forceMount keeps closed answers in the prerendered HTML so
                        crawlers can read them; the class hides the answer while
                        its panel is closed. */}
                    <AccordionContent
                      forceMount
                      className="text-muted-foreground leading-relaxed px-4 pb-6 text-base [[data-state=closed]>&]:hidden"
                    >
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Enquiry */}
        <section
          ref={formSectionRef}
          id="enquiry"
          className="py-20 md:py-30 bg-background scroll-mt-20"
        >
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto items-start">
              <div className="space-y-6">
                <p className="font-mono text-[13px] font-normal tracking-[.14em] uppercase text-accent">Enquire</p>
                <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-[-0.03em] text-foreground leading-tight">
                  Tell us about your business
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  We've drafted a message for the <strong className="text-foreground">{selected.name}</strong>{" "}
                  plan. Fill in the blanks, add your name and contact number, and we'll call you back
                  to discuss your requirement and share a quotation.
                </p>
                <ul className="space-y-4 pt-2">
                  <li className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-accent shrink-0" />
                    <a href={CONTACT.phoneHref} className="text-foreground/80 hover:text-accent transition-colors">
                      {CONTACT.phone}
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-accent shrink-0" />
                    <a href={`mailto:${CONTACT.email}`} className="text-foreground/80 hover:text-accent transition-colors">
                      {CONTACT.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-accent shrink-0" />
                    <span className="text-foreground/80">Support during business hours: {CONTACT.hours}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary rounded-3xl p-8 md:p-10 shadow-xl">
                <PricingEnquiryForm
                  ref={nameRef}
                  plan={plan}
                  onPlanChange={selectPlan}
                  message={message}
                  onMessageChange={editMessage}
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
